import { useCallback, useEffect, useState } from "react";
import Swal from "sweetalert2";
import { brand } from "../../../../config/theme";
import { buildThemeClasses } from "../../theme";
import {
  getAvailability,
  saveAvailability,
  getGoogleStatus,
  WEEKDAYS_LONG_ES,
  type Availability,
  type AvailabilityWindow,
  type GoogleStatus,
} from "../../services/bookingsService";

// ===========================================================================
// Disponibilidad — LIVE editor for the booking rules that power the public
// scheduler: weekly windows, slot length, lead time, timezone, plus the Google
// Calendar connection status (events + Meet link).
// ===========================================================================

// Weekday order Mon..Sun for the editor (keys are "0"=Sun..."6"=Sat).
const WEEKDAY_ORDER = ["1", "2", "3", "4", "5", "6", "0"];

export default function DisponibilidadLive() {
  const tc = buildThemeClasses();

  const [av, setAv] = useState<Availability | null>(null);
  const [google, setGoogle] = useState<GoogleStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [a, g] = await Promise.all([getAvailability(), getGoogleStatus()]);
      setAv(a);
      setGoogle(g);
    } catch (e) {
      const err = e as { message?: string };
      setError(err.message || "No se pudo cargar la disponibilidad");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const patch = (p: Partial<Availability>) => setAv((prev) => (prev ? { ...prev, ...p } : prev));

  const toggleDay = (key: string, enabled: boolean) => {
    if (!av) return;
    const weekly = { ...av.weekly };
    weekly[key] = enabled ? [{ start: "09:00", end: "17:00" }] : [];
    patch({ weekly });
  };

  const setWindow = (key: string, idx: number, field: keyof AvailabilityWindow, value: string) => {
    if (!av) return;
    const weekly = { ...av.weekly };
    const wins = [...(weekly[key] || [])];
    wins[idx] = { ...wins[idx], [field]: value };
    weekly[key] = wins;
    patch({ weekly });
  };

  const addWindow = (key: string) => {
    if (!av) return;
    const weekly = { ...av.weekly };
    weekly[key] = [...(weekly[key] || []), { start: "09:00", end: "13:00" }];
    patch({ weekly });
  };

  const removeWindow = (key: string, idx: number) => {
    if (!av) return;
    const weekly = { ...av.weekly };
    weekly[key] = (weekly[key] || []).filter((_, i) => i !== idx);
    patch({ weekly });
  };

  const save = async () => {
    if (!av) return;
    setSaving(true);
    try {
      const saved = await saveAvailability({
        timezone: av.timezone,
        utcOffset: av.utcOffset,
        slotMinutes: av.slotMinutes,
        minLeadHours: av.minLeadHours,
        maxAdvanceDays: av.maxAdvanceDays,
        weekly: av.weekly,
        googleCalendar: { ...av.googleCalendar },
      });
      if (saved) setAv(saved);
      Swal.fire({ icon: "success", title: "Guardado", timer: 1400, showConfirmButton: false });
    } catch (e) {
      const err = e as { message?: string };
      Swal.fire({ icon: "error", title: "Error", text: err.message || "No se pudo guardar", confirmButtonColor: brand.primary });
    } finally {
      setSaving(false);
    }
  };

  const inputCls = `rounded-lg border px-2 py-1.5 text-sm ${tc.border} ${tc.cardBg} ${tc.text}`;

  if (loading || !av) {
    return (
      <div className="h-full w-full overflow-y-auto p-6">
        <div className={`mx-auto max-w-3xl rounded-2xl border p-10 text-center text-sm ${tc.cardBorder} ${tc.cardBg} ${tc.textSecondary}`}>
          {error ? <span className="text-red-500">{error}</span> : <><i className="ri-loader-4-line mr-2 animate-spin" /> Cargando...</>}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full overflow-y-auto p-6">
      <div className="mx-auto max-w-3xl space-y-5">
        {/* header */}
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500/15 text-primary-500">
            <i className="ri-time-line text-xl" />
          </span>
          <div>
            <h1 className={`text-xl font-bold ${tc.text}`}>Disponibilidad</h1>
            <p className={`text-sm ${tc.textSecondary}`}>Define cuándo se pueden agendar sesiones desde el sitio web</p>
          </div>
        </div>

        {/* Google Calendar status */}
        <div className={`rounded-2xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className={`flex h-10 w-10 items-center justify-center rounded-lg ${google?.connected ? "bg-emerald-500/15 text-emerald-500" : "bg-gray-400/15 text-gray-400"}`}>
                <i className="ri-google-line text-lg" />
              </span>
              <div>
                <p className={`text-sm font-semibold ${tc.text}`}>Google Calendar</p>
                <p className={`text-xs ${google?.connected ? "text-emerald-500" : tc.textSecondary}`}>
                  {google?.connected ? "Conectado · se crean eventos + enlace de Meet" : "No conectado"}
                </p>
              </div>
            </div>
            <span className={`rounded-full px-3 py-1 text-xs font-medium ${google?.connected ? "bg-emerald-500/15 text-emerald-500" : "bg-amber-500/15 text-amber-500"}`}>
              {google?.connected ? "Activo" : "Pendiente"}
            </span>
          </div>
          {!google?.connected && google?.reason && (
            <p className={`mt-3 rounded-lg border border-dashed p-3 text-xs ${tc.border} ${tc.textSecondary}`}>
              <i className="ri-information-line mr-1" />
              {google.reason} El calendario funciona igual sin la conexión; los eventos simplemente no se reflejan en Google.
            </p>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-4">
            <label className={`flex items-center gap-2 text-sm ${tc.text}`}>
              <input
                type="checkbox"
                checked={av.googleCalendar.createMeet}
                onChange={(e) => patch({ googleCalendar: { ...av.googleCalendar, createMeet: e.target.checked } })}
              />
              Crear enlace de Google Meet
            </label>
            <div className="flex items-center gap-2">
              <span className={`text-sm ${tc.textSecondary}`}>Calendario:</span>
              <input
                value={av.googleCalendar.calendarId}
                onChange={(e) => patch({ googleCalendar: { ...av.googleCalendar, calendarId: e.target.value } })}
                className={`${inputCls} w-40`}
              />
            </div>
          </div>
        </div>

        {/* slot settings */}
        <div className={`grid grid-cols-2 gap-4 rounded-2xl border p-4 sm:grid-cols-4 ${tc.cardBorder} ${tc.cardBg}`}>
          <Setting tc={tc} label="Zona horaria">
            <input value={av.timezone} onChange={(e) => patch({ timezone: e.target.value })} className={`${inputCls} w-full`} />
          </Setting>
          <Setting tc={tc} label="Offset UTC">
            <input value={av.utcOffset} onChange={(e) => patch({ utcOffset: e.target.value })} placeholder="-04:00" className={`${inputCls} w-full`} />
          </Setting>
          <Setting tc={tc} label="Duración (min)">
            <select value={av.slotMinutes} onChange={(e) => patch({ slotMinutes: Number(e.target.value) })} className={`${inputCls} w-full`}>
              {[15, 20, 30, 45, 60].map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </Setting>
          <Setting tc={tc} label="Anticipación (h)">
            <input
              type="number"
              min={0}
              value={av.minLeadHours}
              onChange={(e) => patch({ minLeadHours: Number(e.target.value) })}
              className={`${inputCls} w-full`}
            />
          </Setting>
        </div>

        {/* weekly hours */}
        <div className={`rounded-2xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
          <p className={`mb-3 text-sm font-semibold ${tc.text}`}>Horario semanal</p>
          <div className="space-y-2">
            {WEEKDAY_ORDER.map((key) => {
              const wins = av.weekly[key] || [];
              const enabled = wins.length > 0;
              return (
                <div key={key} className={`flex flex-wrap items-center gap-3 rounded-lg border p-2.5 ${tc.border}`}>
                  <label className="flex w-28 items-center gap-2">
                    <input type="checkbox" checked={enabled} onChange={(e) => toggleDay(key, e.target.checked)} />
                    <span className={`text-sm font-medium ${tc.text}`}>{WEEKDAYS_LONG_ES[Number(key)]}</span>
                  </label>
                  {!enabled ? (
                    <span className={`text-xs ${tc.textSecondary}`}>No disponible</span>
                  ) : (
                    <div className="flex flex-1 flex-wrap items-center gap-2">
                      {wins.map((w, idx) => (
                        <div key={idx} className="flex items-center gap-1">
                          <input type="time" value={w.start} onChange={(e) => setWindow(key, idx, "start", e.target.value)} className={inputCls} />
                          <span className={tc.textSecondary}>–</span>
                          <input type="time" value={w.end} onChange={(e) => setWindow(key, idx, "end", e.target.value)} className={inputCls} />
                          {wins.length > 1 && (
                            <button onClick={() => removeWindow(key, idx)} className="rounded p-1 text-red-500 hover:bg-red-500/10">
                              <i className="ri-close-line" />
                            </button>
                          )}
                        </div>
                      ))}
                      <button onClick={() => addWindow(key)} className="rounded p-1 text-primary-500 hover:bg-primary-500/10" title="Agregar franja">
                        <i className="ri-add-line" />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex justify-end">
          <button onClick={save} disabled={saving} className="rounded-lg bg-primary-500 px-6 py-2.5 text-sm font-medium text-on-primary hover:bg-primary-400 disabled:opacity-60">
            {saving ? "Guardando..." : "Guardar disponibilidad"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Setting({ tc, label, children }: { tc: ReturnType<typeof buildThemeClasses>; label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className={`mb-1 block text-xs font-medium ${tc.textSecondary}`}>{label}</label>
      {children}
    </div>
  );
}
